import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

const UGCAgenturBerlin = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate("/?lang=de");
    }, 100);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>UGC Agentur Berlin | AI UGC Creator Berlin | Fuel72</title>
        <meta
          name="description"
          content="UGC Agentur Berlin: Professionelle AI UGC Videos für Berliner Unternehmen. UGC Creator Berlin ohne Influencer. 72 Stunden Lieferung. ✓ Lokal ✓ Schnell ✓ Günstig"
        />
        <meta
          name="keywords"
          content="ugc agentur berlin, ugc creator berlin, ugc berlin, ai ugc berlin, ugc videos berlin, content creator berlin, ugc werbung berlin"
        />
        <link rel="canonical" href="https://www.fuel72.com/ugc-agentur-berlin" />
        
        {/* Hreflang */}
        <link rel="alternate" hrefLang="de" href="https://www.fuel72.com/ugc-agentur-berlin" />
        <link rel="alternate" hrefLang="de-DE" href="https://www.fuel72.com/ugc-agentur-berlin" />
        <link rel="alternate" hrefLang="en" href="https://www.fuel72.com/hire-ugc" />
        <link rel="alternate" hrefLang="x-default" href="https://www.fuel72.com/ugc-agentur-berlin" />
        
        {/* Open Graph */}
        <meta property="og:title" content="UGC Agentur Berlin | AI UGC Creator | Fuel72" />
        <meta property="og:description" content="UGC Videos für Berliner Unternehmen. AI-powered, schnell, professionell." />
        <meta property="og:url" content="https://www.fuel72.com/ugc-agentur-berlin" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="de_DE" />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="DE-BE" />
        <meta name="geo.placename" content="Berlin" />
        <meta name="geo.position" content="52.520008;13.404954" />
        <meta name="ICBM" content="52.520008, 13.404954" />

        {/* Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Fuel72 - UGC Agentur Berlin",
            "description": "Professionelle AI UGC Videos für Berliner Unternehmen. UGC Content erstellen ohne Filmen.",
            "url": "https://www.fuel72.com/ugc-agentur-berlin",
            "telephone": "+4917687870011",
            "email": "hello@fuel72.com",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Berlin",
              "addressRegion": "Berlin",
              "addressCountry": "DE"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "52.520008",
              "longitude": "13.404954"
            },
            "areaServed": {
              "@type": "City",
              "name": "Berlin"
            },
            "priceRange": "€€",
            "openingHours": "Mo-Fr 09:00-18:00"
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Gibt es eine UGC Agentur in Berlin?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja! Fuel72 bietet professionelle UGC Videos für Berliner Unternehmen. Mit AI erstellen wir authentische UGC Inhalte ohne lokale Creator buchen zu müssen."
                }
              },
              {
                "@type": "Question",
                "name": "Was kostet UGC in Berlin?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Mit Fuel72 erhalten Sie 6 professionelle AI UGC Videos + 6 Poster + Captions für nur 297€. Günstiger und schneller als traditionelle Berliner Creator."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <main className="min-h-screen gradient-mesh flex items-center justify-center">
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
            UGC Agentur{" "}
            <span className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 bg-clip-text text-transparent">
              Berlin
            </span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Professionelle AI UGC Videos für Berliner Unternehmen. 
            Restaurants, Shops, Startups – wir machen Sie sichtbar.
          </p>
          
          <div className="max-w-3xl mx-auto text-left space-y-6">
            <h2 className="text-2xl font-bold">UGC für Berliner Unternehmen</h2>
            <p className="text-muted-foreground">
              Berlin ist Deutschlands Startup-Hauptstadt und Trend-Zentrum. Mit Fuel72 bekommen 
              Berliner Unternehmen professionelle UGC Videos, die auf Instagram, TikTok und 
              Meta Ads viral gehen – ohne teure lokale Creator buchen zu müssen.
            </p>
            
            <h2 className="text-2xl font-bold mt-8">Branchen in Berlin</h2>
            <ul className="space-y-2 text-muted-foreground">
              <li>🍔 Restaurants & Cafés in Kreuzberg, Mitte, Prenzlauer Berg</li>
              <li>💪 Fitness Studios & Yoga Spaces</li>
              <li>🛍️ Fashion & Retail in Friedrichstraße</li>
              <li>🚀 Tech Startups & SaaS</li>
              <li>💇 Beauty & Wellness Salons</li>
            </ul>
            
            <h2 className="text-2xl font-bold mt-8">Warum Fuel72 statt Berliner Creator?</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li>✅ <strong>Günstiger</strong> – 297€ statt 500-2000€ pro Creator</li>
              <li>✅ <strong>Schneller</strong> – 72 Stunden statt Wochen</li>
              <li>✅ <strong>Skalierbar</strong> – Unbegrenzt Content produzieren</li>
              <li>✅ <strong>Kein Risiko</strong> – Keine No-Shows oder schlechte Qualität</li>
            </ul>
          </div>
          
          <p className="text-sm text-muted-foreground mt-12">Weiterleitung zur Hauptseite...</p>
        </div>
      </main>
    </>
  );
};

export default UGCAgenturBerlin;
